import {
  AllResponsiblesRes,
  AllSubjectsRes,
  AllUserRes,
  ClassEvent,
  CreateClassEventReq,
  CreateSubjectReq,
  DefaultDataClassEventRes,
  DeleteEventReq,
  EventDataIds,
  ForgotPasswordReq,
  MessageRes,
  PaymentData,
  PendingReportsRes,
  RegisterAdminReq,
  RegisterEmployeeReq,
  RegisterResponsibleReq,
  RegisterStudentReq,
  RegisterTeacherReq,
  ReportEntity,
  ResetPasswordReq,
  ResponsibleRes,
  SubjectEntity,
  TeacherRes,
  TripValueRes,
  UpdateClassReq,
  UpdateReportReq,
  UpdateSubjectReq,
  ReportsDetails,
  DeleteSubjectReq,
  DeleteManySubjectsRes,
  StudentAddressRes,
  CreditData,
  ResponsibleCreditSummaryDto,
  CreateCreditDto,
  FindReportPaymentsReq,
  CustomExcelUserRole,
  UserRoleExcelExclusive,
  UserRole,
  PaymentStatus,
  UpdatePaymentParams,
  ClassEventInfoReq,
} from './api.types';
import { apiClient } from './axios-wrapper';

const api = apiClient;

class ApiService {
  async login(email: string, password: string) {
    return await api.post('/auth/signin', { email, password });
  }

  async logout(userId: number) {
    return await api.post('/auth/signout', { userId });
  }

  async refresh() {
    return await api.post('/auth/refresh', null, { hideToast: true });
  }

  async forgotPassword(forgotPasswordReq: ForgotPasswordReq) {
    return await api.post<ForgotPasswordReq, MessageRes>(
      '/auth/forgot-password',
      forgotPasswordReq
    );
  }

  async resetPassword(resetPasswordReq: ResetPasswordReq) {
    return await api.post<ResetPasswordReq, MessageRes>(
      '/auth/reset-password',
      resetPasswordReq
    );
  }

  async getAllSubjects() {
    return await api.get<SubjectEntity[]>('/subjects');
  }

  async getResponsible(id: number) {
    return await api.get<ResponsibleRes>(`/responsibles/${id}`);
  }

  async getAllResponsibles() {
    return await api.get<AllResponsiblesRes>('/responsibles');
  }

  async registerTeacher(data: RegisterTeacherReq) {
    return await api.post<RegisterTeacherReq, MessageRes>(
      '/auth/signup/teacher',
      data
    );
  }

  async registerResponsibleAndStudent(data: RegisterResponsibleReq) {
    return await api.post<RegisterResponsibleReq, MessageRes>(
      '/auth/signup/responsible',
      data
    );
  }

  async registerStudent(data: RegisterStudentReq) {
    return await api.post<RegisterStudentReq, MessageRes>(
      '/auth/signup/student',
      data
    );
  }

  async getStudentAddress(id: number) {
    return await api.get<StudentAddressRes>(`/students/${id}/address`);
  }

  async registerEmployee(data: RegisterEmployeeReq) {
    return await api.post<RegisterEmployeeReq, MessageRes>(
      '/auth/signup/employee',
      data
    );
  }

  async registerAdmin(data: RegisterAdminReq) {
    return await api.post<RegisterAdminReq, MessageRes>(
      '/auth/signup/admin',
      data
    );
  }

  async createEvent(data: CreateClassEventReq) {
    return await api.post<CreateClassEventReq, ClassEvent>('/classes', data);
  }

  async getDefaultData(data: EventDataIds) {
    return await api.get<DefaultDataClassEventRes>('/classes/default-values/', {
      params: {
        teacherId: data.teacherId,
        studentId: data.studentId,
        subjectId: data.subjectId,
      },
    });
  }

  async getAllEventStudent(id: number, startDate: Date, endDate: Date) {
    return await api.get<ClassEvent[]>(`/classes/student/${id}`, {
      params: {
        startDate,
        endDate,
      },
    });
  }

  async getOrCreateReportEvent(id: number, date: Date) {
    return await api.get<ReportEntity>(`/classes/${id}/reports`, {
      params: {
        date,
      },
    });
  }

  async getReportPayments(
    id: number,
    body: FindReportPaymentsReq
  ): Promise<PaymentStatus[]> {
    return await api.post<FindReportPaymentsReq, PaymentStatus[]>(
      `/classes/${id}/reports-payment-status`,
      body
    );
  }

  async getAllEventsWithReportsStudent(
    id: number,
    startDate: string,
    endDate: string
  ) {
    return await api.get<ClassEvent[]>(`/classes/student/${id}/reports`, {
      params: {
        startDate,
        endDate,
      },
    });
  }

  async getAllEventTeacher(id: number, startDate: Date, endDate: Date) {
    return await api.get<ClassEvent[]>(`/classes/teacher/${id}`, {
      params: {
        startDate,
        endDate,
      },
    });
  }

  async getAllEventsWithReportsTeacher(
    id: number,
    startDate: string,
    endDate: string
  ) {
    return await api.get<ClassEvent[]>(`/classes/teacher/${id}/reports`, {
      params: {
        startDate,
        endDate,
      },
    });
  }

  async updateReport(id: number, data: UpdateReportReq) {
    return await api.patch<UpdateReportReq, ReportEntity>(
      `reports/${id}`,
      data
    );
  }

  async findReport(id: number) {
    return await api.get<ReportEntity>(`reports/${id}`);
  }

  async getAllResponsibleStudents(id: number) {
    return await api.get<AllUserRes>(`responsibles/${id}/students`);
  }

  async deleteEvent(id: number, data: DeleteEventReq) {
    return await api.delete<ClassEvent>(`classes/${id}`, { data });
  }

  async getEvent(id: number) {
    return await api.get<ClassEvent>(`classes/${id}`, { hideToast: true });
  }

  async updateEvent(id: number, data: UpdateClassReq) {
    return await api.patch<UpdateClassReq, ClassEvent>(`classes/${id}`, data);
  }

  async getTeacher(id: number) {
    return await api.get<TeacherRes>(`/teachers/${id}`);
  }

  async findPendingReports(id: number) {
    return await api.get<PendingReportsRes>(`/teachers/${id}/reports/pending`);
  }

  async createSubject(data: CreateSubjectReq) {
    return await api.post<CreateSubjectReq, AllSubjectsRes>('/subjects/', data);
  }

  async updateSubject(id: number, data: UpdateSubjectReq) {
    return await api.patch<UpdateSubjectReq, SubjectEntity>(
      `/subjects/${id}`,
      data
    );
  }

  async deleteManySubjects(data: DeleteSubjectReq) {
    return await api.delete<DeleteManySubjectsRes>('/subjects/', {
      data,
    });
  }

  async getAllPaymentsByResponsible(id: number) {
    return await api.get<PaymentData[]>(`/payments/responsible/${id}`);
  }

  async getAllCreditsByResponsible(id: number) {
    return await api.get<CreditData[]>(`/credit/responsible/${id}`);
  }

  async getResponsibleCreditSummary(id: number) {
    return await api.get<ResponsibleCreditSummaryDto>(
      `/payments/responsible/${id}/credit-summary`
    );
  }

  async createOrRemoveCredit(data: CreateCreditDto) {
    return await api.post<CreateCreditDto, CreditData | PaymentData>(
      '/credit',
      data
    );
  }

  async getPaymentDetails(paymentId: number) {
    return await api.get<ReportsDetails[]>(`/payments/${paymentId}/details`);
  }

  async markAsReceivedInCash(id: number): Promise<PaymentData> {
    return await api.post<undefined, PaymentData>(
      `/payments/${id}/received-in-cash`,
      undefined
    );
  }

  async getValueToTrip() {
    return await api.get<TripValueRes>('/classes/trip-value');
  }

  async cancelPayment(paymentId: number) {
    return await api.delete<PaymentData>(`/payments/${paymentId}`);
  }

  async restorePayment(paymentId: number) {
    return await api.post<null, PaymentData>(
      `/payments/${paymentId}/restore`,
      null
    );
  }

  async getReportSummaryExcel(userId: number, startDate: Date, endDate: Date) {
    return await api.get<BlobPart>(
      `/excel-generator/report-summary/user/${userId}`,
      {
        responseType: 'blob',
        params: { startDate: startDate, endDate: endDate },
      }
    );
  }

  async getUsersExcel(role?: CustomExcelUserRole, active?: boolean) {
    const isResponsibleStudent =
      role === UserRoleExcelExclusive.RESPONSIBLE_STUDENT;
    if (isResponsibleStudent) role = UserRole.RESPONSIBLE;
    if (
      role &&
      (role === UserRoleExcelExclusive.ALL ||
        role === UserRoleExcelExclusive.UNKNOWN)
    )
      role = undefined;
    return await api.get<BlobPart>('/excel-generator/users', {
      responseType: 'blob',
      params: {
        role,
        active,
        isResponsibleStudent,
      },
    });
  }

  async updatePayment(
    paymentId: number,
    updatePaymentParams: UpdatePaymentParams
  ) {
    return await api.patch<UpdatePaymentParams, PaymentData>(
      `/payments/${paymentId}`,
      updatePaymentParams
    );
  }

  async getReportsExcelByDate(
    startDate: Date,
    endDate: Date,
    includeSimulatedReports = false
  ) {
    return await api.get<BlobPart>('/excel-generator/reports', {
      responseType: 'blob',
      params: { startDate, endDate, includeSimulatedReports },
    });
  }

  async getReportById(id: number): Promise<ReportEntity> {
    return await api.get<ReportEntity>(`/reports/${id}`);
  }

  async checkConflict(classEventInfo: ClassEventInfoReq) {
    return await api.post<ClassEventInfoReq, boolean>(
      '/classes/has-conflicts',
      classEventInfo
    );
  }
}

export const apiService = new ApiService();
